import moment from 'moment';
import { Bookings, UpdateElitetutorVacanciesRequest, Vacancies } from 'logic/api-models/api-models';
import {
  SlotState,
  TimeSlot,
  ViewAs,
} from 'components/TutoringDateTimePicker/TutoringDateTimePicker.types';
import { logError } from 'logic/log/log.logic';
import { translate, translationKeys } from 'logic/translations/translations.service';

export const TIME_SLOT_DURATION_IN_MINUTES = 30;

export const PICKABLE_START_TIME = '06:00';
export const POSSIBLE_PICK_DURATION_DAYS = 7;
export const POSSIBLE_MOBILE_PICK_DURATION_DAYS = 4;
export const POSSIBLE_TIME_SLOTS = 34;
export const PRIMETIME_START = '14:30';
export const PRIMETIME_END = '20:00';
export const WEEKSTART_OFFSET_IN_MINUTES = 60 * 13;
export const WEEKSTART_OFFSET_TUTOR_WHEN_BOOKING_IN_MINUTES = 30;
export const VACANCY_SLOT_IGNORE_REMINDER_THRESHOLD = 12;
export const VACANCY_SLOT_MOBILE_IGNORE_REMINDER_THRESHOLD = 8;

export const initializeWeek = (
  currentDate: moment.Moment,
  vacancyList: Vacancies | undefined,
  possiblePickDuration: number,
  bookedList: Bookings | undefined,
  viewAs: ViewAs,
  currentOffsetTime: moment.Moment,
  isAdmin?: boolean,
) => {
  const currentWeekStart = currentDate.clone().startOf('day');
  let initSelectedTimeSlots: TimeSlot[] = [];
  let currentWeekDayList: moment.Moment[] = [];

  for(let i = 0; i < possiblePickDuration; i++) {
    currentWeekDayList.push(moment(currentWeekStart).add(i, 'days'));
    initSelectedTimeSlots.push({
      date: moment(currentWeekStart).add(i, 'days').format('YYYY-MM-DD'),
      slots: [],
    });
    const dayTimeInMinutes = currentWeekDayList[i].clone().add(1, 'days').diff(currentWeekDayList[i], 'minutes');

    for(let k = 0; k < dayTimeInMinutes / TIME_SLOT_DURATION_IN_MINUTES; k++) {
      initSelectedTimeSlots[i].slots.push(SlotState.OPEN);
    }
  }

  vacancyList?.forEach((vacancy) => {
    const indexOfSelectedTimeSlot = initSelectedTimeSlots.findIndex((timeslots) => (timeslots.date === moment(vacancy?.start).format('YYYY-MM-DD')));
    if (indexOfSelectedTimeSlot > -1 ) {
      const startMinutesOfSlots = moment(vacancy?.start).diff(moment(vacancy?.start).startOf('day'), 'minutes');
      const startIndexOfSlots = (startMinutesOfSlots % TIME_SLOT_DURATION_IN_MINUTES === 0) ? startMinutesOfSlots / TIME_SLOT_DURATION_IN_MINUTES : null;

      const diffInMinutes = moment(vacancy?.end).diff(moment(vacancy?.start), 'minutes');
      const diffInSlots = (diffInMinutes % TIME_SLOT_DURATION_IN_MINUTES === 0) ? diffInMinutes / TIME_SLOT_DURATION_IN_MINUTES : null;

      if (diffInSlots && startIndexOfSlots != null) {
        fillSelectedTimeSlots(
          initSelectedTimeSlots,
          indexOfSelectedTimeSlot,
          startIndexOfSlots,
          diffInSlots,
          possiblePickDuration,
          SlotState.AVAILABLE
        );
      }
    }
  });

  if (viewAs === ViewAs.ELITE_SEEKER || viewAs === ViewAs.ELITE_TUTOR_BOOKING) {
    bookedList?.forEach((vacancy) => {
      const indexOfSelectedTimeSlot = initSelectedTimeSlots.findIndex((timeslots) => (timeslots.date === moment(vacancy?.start).format('YYYY-MM-DD')));
      if (indexOfSelectedTimeSlot > -1 ) {
        const startMinutesOfSlots = moment(vacancy?.start).diff(moment(vacancy?.start).startOf('day'), 'minutes');
        const startIndexOfSlots = (startMinutesOfSlots % TIME_SLOT_DURATION_IN_MINUTES === 0) ? startMinutesOfSlots / TIME_SLOT_DURATION_IN_MINUTES : null;

        const diffInMinutes = moment(vacancy?.end).diff(moment(vacancy?.start), 'minutes');
        const diffInSlots = (diffInMinutes % TIME_SLOT_DURATION_IN_MINUTES === 0) ? diffInMinutes / TIME_SLOT_DURATION_IN_MINUTES : null;

        if (diffInSlots && startIndexOfSlots) {
          fillSelectedTimeSlots(
            initSelectedTimeSlots,
            indexOfSelectedTimeSlot,
            startIndexOfSlots,
            diffInSlots,
            possiblePickDuration,
            viewAs === ViewAs.ELITE_TUTOR_BOOKING ? SlotState.UNAVAILABLE : SlotState.OPEN,
          );
        }
      }
    });
  }

  if (!isAdmin) {
    const current = moment(); //TODO localtime
    const currentDayStart = current.clone().startOf('day');

    const indexOfSelectedTimeSlot = initSelectedTimeSlots.findIndex((timeslots) => (timeslots.date === moment(currentDayStart).format('YYYY-MM-DD')));
    if (indexOfSelectedTimeSlot > -1 ) {
      //startIndexOfSlots = start of unavailable time for normal user || so e.g. timespan is  00:00 pm -> current -> current + 12h
      const startIndexOfSlots = 0;

      const diffInMinutes = moment(currentOffsetTime).diff(currentDayStart, 'minutes');
      const diffInSlots = (diffInMinutes % TIME_SLOT_DURATION_IN_MINUTES === 0) ? diffInMinutes / TIME_SLOT_DURATION_IN_MINUTES : null;

      if (diffInSlots) {
        fillSelectedTimeSlots(
          initSelectedTimeSlots,
          indexOfSelectedTimeSlot,
          startIndexOfSlots,
          diffInSlots,
          possiblePickDuration,
          SlotState.UNAVAILABLE,
        );
      }
    }
  }

  if (isAdmin) {
    const current = moment(); //TODO localtime
    const currentDayStart = current.clone().startOf('day');

    const indexOfSelectedTimeSlot = initSelectedTimeSlots.findIndex((timeslots) => (timeslots.date === moment(currentDayStart).format('YYYY-MM-DD')));
    if (indexOfSelectedTimeSlot > -1 ) {
      //startIndexOfSlots = start of unavailable time for normal user || so e.g. timespan is  00:00 pm -> current -> current + 12h
      const startIndexOfSlots = 0;

      const diffInMinutes = moment(roundToNextSlot(current)).diff(currentDayStart, 'minutes');
      const diffInSlots = (diffInMinutes % TIME_SLOT_DURATION_IN_MINUTES === 0) ? diffInMinutes / TIME_SLOT_DURATION_IN_MINUTES : null;

      if (diffInSlots) {
        fillSelectedTimeSlots(
          initSelectedTimeSlots,
          indexOfSelectedTimeSlot,
          startIndexOfSlots,
          diffInSlots,
          possiblePickDuration,
          SlotState.UNAVAILABLE,
        );
      }
    }
  }

  return { initSelectedTimeSlots, currentWeekDayList };
};

const fillSelectedTimeSlots = (timeSlots: TimeSlot[], indexOfTimeSlot: number, startIndex: number, diff: number, possiblePickDuration: number, slotstate: SlotState) => {
  if (indexOfTimeSlot >= possiblePickDuration) return;
  for (let i = startIndex; diff > 0; i++) {

    if (i >= timeSlots[indexOfTimeSlot].slots.length) {
      fillSelectedTimeSlots(timeSlots, indexOfTimeSlot + 1, 0, diff, possiblePickDuration, slotstate);
      return;
    } else {
      timeSlots[indexOfTimeSlot].slots[i] = slotstate;
      diff--;
    }
  }
};

const calculateReplaceDurationInMinutes = (currentOffsetTime: moment.Moment, endOfTimePicker: moment.Moment): number => {
  return endOfTimePicker.diff(currentOffsetTime, 'minutes');
};

export const mapSelectedTimeslotsToVacanciesRequest = (
    selectedTimeSlots: TimeSlot[],
    possibleTimeDurationInDays: number,
    currentOffsetTime: moment.Moment,
    isAdmin?: boolean,
  ): UpdateElitetutorVacanciesRequest => {

  const startOfTimePicker =  moment(selectedTimeSlots[0].date, 'YYYY-MM-DD');
  const startOfAvailableTimePicker = isAdmin ? roundToNextSlot(moment()) : currentOffsetTime;
  let timeSlotDTO: UpdateElitetutorVacanciesRequest = {
    replaceStartsAt: startOfAvailableTimePicker.format(),
    replaceDurationInMinutes: calculateReplaceDurationInMinutes(startOfAvailableTimePicker, startOfTimePicker.clone().add(possibleTimeDurationInDays, 'days')),
    slotSizeInMinutes: TIME_SLOT_DURATION_IN_MINUTES,
    newVacanciesAsSlots: [],
  };

  selectedTimeSlots.forEach((selectedTimeSlot: TimeSlot) => {
    selectedTimeSlot.slots.forEach((slot => {
      if (slot === SlotState.OPEN) timeSlotDTO.newVacanciesAsSlots?.push(0);
      if (slot === SlotState.AVAILABLE)timeSlotDTO.newVacanciesAsSlots?.push(1);
    }));
  });
  return timeSlotDTO;
};

//case i=0 and i = selectedTimeSlot.length not  tested
export const validateSelectedTimeSlots = (selectedTimeSlots: TimeSlot[]) => {
  const errorList: boolean[] = [];

  selectedTimeSlots.forEach((selectedTimeSlot) => {
    selectedTimeSlot.slots.forEach((slot: SlotState, i) => {
      if (slot === SlotState.AVAILABLE && selectedTimeSlot.slots[i-1] === SlotState.OPEN && selectedTimeSlot.slots[i+1] === SlotState.OPEN) errorList.push(true);
      if (slot === SlotState.BOOKED && selectedTimeSlot.slots[i-1] !== SlotState.BOOKED && selectedTimeSlot.slots[i+1] !== SlotState.BOOKED) errorList.push(true);
    });
  });

  if (errorList.length > 0) {
    logError(translate(translationKeys.timeSlotPicker.selectionError));
    return false;
  }
  return true;
};

export const isPrimetime = (time: moment.Moment) => {
  return time.isBetween(moment(PRIMETIME_START, 'HH:mm'), moment(PRIMETIME_END, 'HH:mm'), undefined, '[)');
};

export const isPrimeday = (time: moment.Moment) => {
  if (time.isoWeekday() === 6 || time.isoWeekday() === 7) return true;
};

export const roundToNextSlot = (now: moment.Moment) => {
  const nowNextMinute = now.clone().startOf('minutes');
  if (now.unix() !== nowNextMinute.unix()) {
    nowNextMinute.add(1, 'minute');
  }
  return now.clone().startOf('hours').add(Math.ceil(nowNextMinute.minute() / TIME_SLOT_DURATION_IN_MINUTES) * TIME_SLOT_DURATION_IN_MINUTES, 'minutes');
};

export const countSelectedTimeSlots = (selectedTimeSlots: TimeSlot[]) => {
  let selectedTimeSlotCounter = 0;

  selectedTimeSlots.forEach((selectedTimeSlot) => {
    selectedTimeSlot.slots.forEach((slot: SlotState, i) => {
      if (slot === SlotState.AVAILABLE) selectedTimeSlotCounter++;
    });
  });

  return selectedTimeSlotCounter;
};
